import React, { useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { Box, Icon } from '@mui/material'
import { eachDayOfInterval, parse, format } from 'date-fns'

// Local imports
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import FillCreditLeaveView from './fillCreditLeaveView'
import ConfirmLeaveView from './confirmLeaveView'
import Loading from 'components/dumb/loading/Loading'
import EmptyData from 'module/leave/pages/createRequest/components/emptyData'

// API services
import { useGetEmployeeEntitlement } from 'services/api/modules/leave/entitlement'
import { usePostNewLeaveCreditRequest } from 'services/api/modules/leave/leaveRequest'
import { useHrMyAccount } from 'services/api/hr-api/hrHooks'
import getLeaveRequestErrorMessage from 'module/leave/util/getLeaveRequestErrorMessage'

const CreateCreditRequestModal = () => {
  const navigate = useNavigate()
  const [pageState, setPageState] = useState('FILL_LEAVE')
  const [submitError, setSubmitError] = useState('')
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)

  const { data, isLoading } = useGetEmployeeEntitlement()
  const { data: myAccountData } = useHrMyAccount()
  const creditData = data?.leave_credit[0]

  const mutation = usePostNewLeaveCreditRequest()

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().required('Please select a date'),
    endDate: Yup.string().required('End Date Required'),
    duration: Yup.number().required('Duration is required'),
    reason: creditData?.leave_policy?.reason_required
      ? Yup.string().required('Reason is required')
      : Yup.string().optional(),
  })

  const handleCloseModal = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    navigate(-1)
  }

  const handleErrorClose = () => {
    setIsErrorModalOpen(false)
  }

  if (pageState === 'SUCCESSFUL_REQUEST') {
    return (
      <AdxInfoModal
        open={true}
        muiIcon="check_circle"
        iconColor="alternate.main"
        title="Leave request submitted"
        subtitle={`Your Leave Credit Request has been successfully submitted.`}
        footerButtons
        firstBtnLabel={'Return to credit request'}
        firstBtnHandle={() => navigate(-1)}
      />
    )
  }

  if (isLoading) {
    return <Loading show />
  }
  return (
    <AdxFormModal
      open={true}
      onClose={handleCloseModal}
      handleClose={handleCloseModal}
      disableEscapeKeyDown
      disableAutoFocus
      title="Request for Leave Credit"
      subtitle={null}
      icon={<Icon>calendar_today</Icon>}
      childPadding={false}
      sxModal={{
        width: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 24px)', md: '600px' },
        minWidth: { xs: 'calc(100vw - 24px)', md: '600px' },
        maxWidth: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 24px)', md: '600px' },
        maxHeight: { xs: 'calc(100dvh - 24px)', sm: '90vh' },
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '10px',
      }}
    >
      {creditData ? (
        <Formik
          enableReinitialize
          initialValues={{
            startDate: '',
            endDate: '',
            duration: '',
            reason: '',
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            if (pageState === 'FILL_LEAVE') {
              setSubmitError('')
              setIsErrorModalOpen(false)
              setPageState('CONFIRM_DETAILS')
            }
            if (pageState === 'CONFIRM_DETAILS') {
              const dateArr = eachDayOfInterval({
                start: parse(values.startDate, `dd/MM/yyyy`, new Date()),
                end: parse(values.endDate, `dd/MM/yyyy`, new Date()),
              })
              const postData = {
                leave_policy_id: creditData.leave_policy.id,
                reason: values.reason,
                dates: dateArr.map((date) => ({
                  date: format(date, `yyyy-MM-dd`),
                  type: 'Full Day',
                })),
              }

              setSubmitError('')
              setIsErrorModalOpen(false)
              mutation.mutate(postData, {
                onSuccess: () => setPageState('SUCCESSFUL_REQUEST'),
                onError: (error) => {
                  setSubmitError(
                    getLeaveRequestErrorMessage({
                      errorMessage: error?.data?.message,
                      selectedLeave: creditData,
                      myAccountData,
                    })
                  )
                  setIsErrorModalOpen(true)
                },
              })
            }
          }}
        >
          {(formikProps) => (
            <>
              <Form>
                {pageState === 'FILL_LEAVE' && (
                  <FillCreditLeaveView
                    formikProps={formikProps}
                    reasonRequired={creditData?.leave_policy?.reason_required}
                  />
                )}

                {pageState === 'CONFIRM_DETAILS' && (
                  <ConfirmLeaveView
                    formikProps={formikProps}
                    setPageState={setPageState}
                    mutation={mutation}
                    setSubmitError={setSubmitError}
                  />
                )}
              </Form>
            </>
          )}
        </Formik>
      ) : (
        <EmptyData
          icon="priority_high"
          title="No Leave Credit Policy Found"
          message="Please contact your HR for more information"
          fontSize={50}
          sx={{ minHeight: '75vh' }}
        />
      )}
      <AdxInfoModal
        variant="error"
        open={isErrorModalOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleErrorClose()
          }
        }}
        handleClose={handleErrorClose}
        muiIcon="error"
        iconColor="error.main"
        title="Unable to submit leave credit request"
        subtitle={submitError}
        footerButtons
        firstBtnLabel="Cancel"
        firstBtnHandle={handleErrorClose}
      />
    </AdxFormModal>
  )
}

export default CreateCreditRequestModal
